import { useRef, useEffect, useState, useCallback } from 'react';
import type { MouseEvent } from 'react';

import loadable from '@loadable/component';
import BpkButton, {
  SIZE_TYPES,
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  withButtonAlignment,
  withRtlSupport,
} from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ArrowUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-up';
import {
  BpkBox,
  BpkFlex,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classnames from 'classnames';
import { GLOBAL } from 'saddlebag-browser';

import useStickySearchControlsHeight, {
  getStickySearchControlsHeightAt,
} from '../../hooks/useStickySearchControlsHeight';
import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { HorizontalNavigationProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/HorizontalNavigation';

import STYLES from './HorizontalNavigation.module.scss';

const AlignedArrowUpIcon = withButtonAlignment(withRtlSupport(ArrowUpIcon));

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const $window = GLOBAL.getWindow();
const $document = GLOBAL.getDocument();
const FIXED_SPACING = 20;
const TOLERANCE = 1;

const HorizontalNavigation = (props: HorizontalNavigationProps) => {
  const { button, firstItemActiveOnTop, isHiddenWhenNotStickToTop, navList } =
    props;
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const placeholderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLElement | null>(null);
  const [isSticky, setSticky] = useState(false);
  const stickySearchControlsHeight = useStickySearchControlsHeight();
  const [navListToRender, setNavListToRender] = useState(navList);
  // Set default active status to be not active
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [hideBackToTopButton, setHideBackToTopButton] = useState(false);
  // whether scrolling is currently in the navigation bar
  const [isScrollingNavbar, setIsScrollingNavbar] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const windowWidthLimit = 768;
      if ($window.innerWidth < windowWidthLimit) {
        setHideBackToTopButton(true);
      } else {
        setHideBackToTopButton(false);
      }
    };

    $window.addEventListener('resize', handleResize);

    return () => {
      $window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (
        isScrollingNavbar &&
        itemsRef.current &&
        Math.abs(event.deltaX) === 0
      ) {
        event.preventDefault();
        // Scroll up and down: Navigation bar Scroll left and right
        // Converts a vertical scroll amount to a horizontal scroll
        itemsRef.current.scrollLeft += event.deltaY;

        // Check if the scroll reaches the boundary
        // Use Math.abs to avoid the scrollWidth and clientWidth calculations can cause floating-point rounding errors
        const scrollDeviation = Math.abs(
          Math.round(itemsRef.current.scrollLeft) -
            Math.round(
              itemsRef.current.scrollWidth - itemsRef.current.clientWidth,
            ),
        );
        if (itemsRef.current.scrollLeft === 0 || scrollDeviation <= TOLERANCE) {
          setIsScrollingNavbar(false); // The boundary is reached, and page scrolling resumes
        }
      }
    },
    [isScrollingNavbar],
  );

  useEffect(() => {
    $window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      $window.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, isScrollingNavbar]);

  useEffect(() => {
    // Should render navs that the relevant components are already rendered on the page
    // The code:
    // https://github.com/skyscanner/falcon/blob/54e5c2b1ec4a1c7fd5c53f356cee42c9c18b0bf6/apps/landing-pages-webapp/src/components/FeatureContainer/FeatureContainer.tsx#L33
    // shows that the FeatureContainer always puts the React component node as a child node of the <div id=REACT_COMPONENT_NAME>.
    // As a result, we should not only check if element exists by id, but also check whether the element has child nodes.
    // In this way we cover the cases where the app returns non-null props and the client returns null element by some empty check.
    // For example:
    // client: https://github.com/skyscanner/falcon/blob/808da5e293be22b73946e195dd8b6090aeb3a67d/apps/landing-pages-webapp/src/components/HotelsByStarRating/HotelsByStarRating.tsx#L12
    // app: https://github.com/skyscanner/falcon/blob/d4302259fde8d1075fa50fa6374a9589650a8aa2/apps/landing-pages/components/localRenderers/hotelByStarRating/index.ts#L315
    setNavListToRender(
      navList.filter((component) => {
        const element = $document.getElementById(component.id);
        if (!element) {
          return false;
        }
        return element.hasChildNodes();
      }),
    );
  }, [navList]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.boundingClientRect.height === 0) {
        setSticky(false);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  useEffect(() => {
    const loadSticky = () => {
      if (placeholderRef.current) {
        const { top } = placeholderRef.current.getBoundingClientRect();

        if (top <= stickySearchControlsHeight) {
          setSticky(true);
        } else {
          setSticky(false);
          setSelectedIndex(-1);
        }
      }
    };

    const loadSelectedIndex = () => {
      // Get container height once
      let containerHeight: number;
      if (containerRef.current) {
        const { height } = containerRef.current.getBoundingClientRect();
        containerHeight = height;
      }

      let navigationBarTop: number;
      if (placeholderRef.current) {
        navigationBarTop = placeholderRef.current.getBoundingClientRect().top;
      }

      const itemActiveStates: boolean[] = [];
      navListToRender.forEach((nav, index) => {
        if (!nav || !nav.id) {
          return;
        }

        const elm = $document.getElementById(nav.id);
        if (elm) {
          const rect = elm.getBoundingClientRect();
          if (
            navigationBarTop > stickySearchControlsHeight &&
            firstItemActiveOnTop &&
            index === 0
          ) {
            itemActiveStates.push(true);
            setSelectedIndex(0);
            itemsRef.current?.scrollTo({ left: 0 });
          } else if (
            rect.top <=
              containerHeight + stickySearchControlsHeight + FIXED_SPACING &&
            rect.bottom >= containerHeight + stickySearchControlsHeight &&
            rect.height !== 0
          ) {
            itemActiveStates.push(true);
            index !== selectedIndex && setSelectedIndex(index);

            const navItem = $document.getElementById(
              `${nav.id}HorizontalNavigation__item`,
            );
            if (navItem) {
              navItem.scrollIntoView({
                inline: 'center',
              });
            }
          } else {
            itemActiveStates.push(false);
          }
        }
      });

      // When no nav ids are matched, remove all active states
      if (
        itemActiveStates.length &&
        itemActiveStates.every((itemState) => !itemState)
      ) {
        setSelectedIndex(-1);
      }
    };

    const handleScroll = () => {
      loadSticky();
      loadSelectedIndex();
    };

    handleScroll();

    $window.addEventListener('scroll', handleScroll);
    return () => {
      $window.removeEventListener('scroll', handleScroll);
    };
  }, [
    firstItemActiveOnTop,
    navListToRender,
    selectedIndex,
    stickySearchControlsHeight,
  ]);

  const handleClick =
    (navIndex: number) => (evt: MouseEvent<HTMLAnchorElement>) => {
      evt.preventDefault();

      navIndex !== selectedIndex && setSelectedIndex(navIndex);

      const target = $document.getElementById(navListToRender[navIndex].id);
      if (target) {
        if (containerRef.current) {
          evt.currentTarget.scrollIntoView({
            inline: 'end',
          });

          const { height } = containerRef.current.getBoundingClientRect();
          const boxShadowHeight = 3;
          const scrollY =
            target.getBoundingClientRect().top +
            $window.pageYOffset -
            height +
            boxShadowHeight -
            FIXED_SPACING;
          // Ensure scroll to the exact component
          $window.scrollTo({
            top: scrollY - getStickySearchControlsHeightAt(scrollY),
            behavior: 'smooth',
          });
        }
      }
    };

  const onButtonClick = (btn: string) => {
    $window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Listen for mouse entry and departure events from the navigation bar
  const handleMouseEnter = useCallback(() => {
    setIsScrollingNavbar(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsScrollingNavbar(false);
  }, []);

  if (!navListToRender?.length) return null;
  return (
    // Height holder: keeps the page from jumping when the container becomes
    // position: fixed. 3rem === 48px (the container height).
    <BpkBox
      ref={placeholderRef}
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      height={isHiddenWhenNotStickToTop ? undefined : '3rem'}
    >
      <BpkVessel
        as="div"
        ref={containerRef}
        style={isSticky ? { top: stickySearchControlsHeight } : undefined}
        className={classnames(STYLES.HorizontalNavigation__container, {
          [STYLES.HorizontalNavigation__fixed]: isSticky,
          [STYLES.HorizontalNavigation__static]: !isSticky,
          [STYLES.HorizontalNavigation__hidden]:
            isHiddenWhenNotStickToTop && !isSticky,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <BpkBox width="100%" maxWidth="76.5rem" margin="auto">
          <BpkMobileScrollContainer
            scrollerRef={(element) => {
              itemsRef.current = element;
            }}
          >
            <BpkFlex minWidth="100%">
              {navListToRender.map(
                (nav, index) =>
                  nav.id && (
                    <a
                      data-tracking-element-id={`${rawComponentName}_item_${index}`}
                      data-tracking-common-params={buildMBDParam(
                        `${rawComponentName}_item`,
                        { trigger_component: nav.id },
                      )}
                      key={nav.id}
                      href={`#${nav.id}`}
                      id={`${nav.id}HorizontalNavigation__item`}
                      className={classnames(STYLES.HorizontalNavigation__item, {
                        [STYLES['HorizontalNavigation__item-selected']]:
                          selectedIndex === index,
                      })}
                      onClick={handleClick(index)}
                    >
                      {nav.content}
                    </a>
                  ),
              )}
              {button && isSticky && (
                <BpkVessel
                  className={classnames(STYLES.HorizontalNavigation__button, {
                    [STYLES['HorizontalNavigation__button-hidden']]:
                      hideBackToTopButton,
                  })}
                >
                  <BpkButton
                    data-tracking-element-id={`${rawComponentName}_cta`}
                    data-tracking-common-params={buildMBDParam(
                      `${rawComponentName}_cta`,
                    )}
                    size={SIZE_TYPES.small}
                    href=""
                    rel="nofollow"
                    onClick={() => onButtonClick(button)}
                    type={BUTTON_TYPES.primary}
                  >
                    {button}
                    <AlignedArrowUpIcon
                      className={STYLES['HorizontalNavigation__arrow-up-icon']}
                    />
                  </BpkButton>
                </BpkVessel>
              )}
            </BpkFlex>
          </BpkMobileScrollContainer>
        </BpkBox>
      </BpkVessel>
    </BpkBox>
  );
};

export default (props: HorizontalNavigationProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.HORIZONTAL_NAVIGATION,
      })
    }
  >
    <HorizontalNavigation {...props} />
  </IntersectionObserverWrapper>
);
